import { motion } from "framer-motion";

const TerminalMotif = ({ accent, compact, active }) => (
  <div className={`rounded-2xl border border-white/10 bg-[#0a0f0a] ${compact ? "p-4 text-[10px]" : "p-6 text-sm"} font-mono shadow-2xl transition-colors duration-500`} data-testid="motif-terminal">
    <div className={`${compact ? "mb-3" : "mb-5"} flex gap-2`}>
      <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
    </div>
    {(compact
      ? ["$ snapx deploy --production", "✓ build completed in 38s", "✓ lighthouse: 98 performance"]
      : ["$ snapx deploy --production", "✓ build completed in 38s", "✓ lighthouse: 98 performance", "✓ zero downtime release"]
    ).map((line, i) => (
      <motion.p
        key={line}
        initial={{ opacity: 0, x: -12 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 + i * 0.3 }}
        className="mt-1.5"
        style={{ color: i === 0 ? "#ffffff" : accent }}
      >
        {line}
      </motion.p>
    ))}
    {active && (
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1.5 text-white/70">
        ▲ shipping your idea…
      </motion.p>
    )}
    <p className="mt-1.5 text-white">
      $ <span className="inline-block h-3 w-2 animate-caret-blink" style={{ background: accent, animationDuration: active ? "0.45s" : "1.1s" }} />
    </p>
  </div>
);

const PhoneMotif = ({ accent, compact, active }) => (
  <div className={compact ? "pb-1 pt-4" : ""}>
    <div
      className={`mx-auto animate-float-y rounded-[2rem] border bg-[#0b0f14] p-2 shadow-2xl transition-[border-color] duration-500 ${compact ? "w-32" : "w-52"} ${active ? "border-white/40" : "border-white/15"}`}
      style={{ animationDuration: active ? "1.8s" : "6s" }}
      data-testid="motif-phone"
    >
      <div className={`rounded-[1.6rem] bg-[#101820] ${compact ? "p-2.5" : "p-4"}`}>
        <div className="mx-auto mb-3 h-3.5 w-14 rounded-full bg-black" />
        {(compact ? [70, 90] : [70, 45, 90, 60]).map((w, i) => (
          <div key={i} className={`rounded-lg bg-white/[0.05] ${compact ? "mb-2 p-2" : "mb-3 p-3"}`}>
            <motion.div
              className="h-1.5 rounded"
              style={{ background: i === (compact ? 1 : 2) ? accent : "rgba(255,255,255,0.15)" }}
              animate={{ width: `${w}%`, opacity: active ? [1, 0.45, 1] : 1 }}
              transition={active ? { duration: 0.7, repeat: Infinity, delay: i * 0.12 } : { duration: 0.4 }}
            />
            <div className="mt-1.5 h-1.5 w-1/3 rounded bg-white/10" />
          </div>
        ))}
        <motion.div
          className={`mt-3 rounded-full text-center font-bold text-ink ${compact ? "py-1.5 text-[9px]" : "py-2.5 text-xs"}`}
          style={{ background: accent }}
          animate={{ scale: active ? [1, 1.06, 1] : 1 }}
          transition={active ? { duration: 0.8, repeat: Infinity } : { duration: 0.3 }}
        >
          Install
        </motion.div>
      </div>
    </div>
  </div>
);

const GridMotif = ({ accent, compact, active }) => (
  <div
    className={`relative overflow-hidden rounded-2xl border border-white/10 ${compact ? "p-4" : "p-8"}`}
    style={{ backgroundImage: `linear-gradient(${accent}22 1px, transparent 1px), linear-gradient(90deg, ${accent}22 1px, transparent 1px)`, backgroundSize: compact ? "24px 24px" : "40px 40px" }}
    data-testid="motif-grid"
  >
    <div className={`space-y-2 font-mono ${compact ? "text-[9px]" : "text-xs"} text-white/70`}>
      {(compact ? [["uptime", "99.98%"], ["entities", "1,204 mapped"], ["audit trail", "complete"]] : [["uptime", "99.98%"], ["entities", "1,204 mapped"], ["reports", "real-time"], ["audit trail", "complete"]]).map(([k, v], i) => (
        <motion.div
          key={k}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          animate={{ x: active ? 6 : 0 }}
          transition={{ delay: compact ? 0.3 + i * 0.15 : 0.3 + i * 0.15, duration: 0.35 }}
          className={`flex justify-between rounded border bg-ink/80 ${compact ? "px-2.5 py-1.5" : "px-4 py-3"} ${active ? "border-white/25" : "border-white/10"}`}
        >
          <span>{k}</span><span style={{ color: accent }}>{v}</span>
        </motion.div>
      ))}
    </div>
  </div>
);

const BrandMotif = ({ accent, compact, active }) => (
  <div className={`relative flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.02] ${compact ? "py-6" : "py-16"}`} data-testid="motif-brand">
    <motion.span
      className={`font-serif-display font-light italic leading-none ${compact ? "text-7xl" : "text-[10rem]"}`}
      style={{ color: accent }}
      animate={{ rotate: active ? -5 : 0, scale: active ? 1.08 : 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 14 }}
    >
      Aa
    </motion.span>
    <div className={`absolute ${compact ? "bottom-3 left-3" : "bottom-6 left-6"} flex gap-2`}>
      {[accent, "#ffffff", "#fbbf24", "#38bdf8"].map((c, i) => (
        <motion.span
          key={c}
          whileHover={{ scale: 1.3 }}
          className={`${compact ? "h-3.5 w-3.5" : "h-6 w-6"} rounded-full border border-white/20`}
          style={{ background: c }}
          animate={active ? { y: [0, -6, 0] } : { y: 0 }}
          transition={active ? { duration: 0.6, repeat: Infinity, delay: i * 0.1 } : { duration: 0.3 }}
        />
      ))}
    </div>
  </div>
);

const MotionMotif = ({ accent, compact, active }) => (
  <div className={`flex ${compact ? "h-36" : "h-64"} items-center justify-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]`} data-testid="motif-motion">
    {[0, 1, 2, 3, 4].map((i) => (
      <motion.span
        key={i}
        className={`${compact ? "w-4" : "w-6"} rounded-full`}
        style={{ background: i === 2 || active ? accent : "rgba(255,255,255,0.12)" }}
        animate={{ height: ["30%", "85%", "30%"] }}
        transition={{ duration: active ? 0.8 : 2.2, repeat: Infinity, delay: i * (active ? 0.1 : 0.25), ease: "easeInOut" }}
      />
    ))}
  </div>
);

const ChartMotif = ({ accent, compact, active }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] ${compact ? "p-4" : "p-8"}`} data-testid="motif-chart">
    <div className={`flex ${compact ? "h-28" : "h-48"} items-end gap-2`}>
      {["30%", "45%", "38%", "62%", "55%", "80%", "72%", "95%"].map((h, i) => (
        <motion.div
          key={i}
          className="flex-1 origin-bottom animate-bar-grow rounded-t"
          style={{ height: h, background: i >= 6 ? accent : "rgba(255,255,255,0.12)", animationDelay: `${i * 0.1}s` }}
          animate={{ opacity: active ? [1, 0.45, 1] : 1 }}
          transition={active ? { duration: 0.7, repeat: Infinity, delay: i * 0.07 } : { duration: 0.3 }}
        />
      ))}
    </div>
    <div className="mt-3 flex justify-between text-[9px] uppercase tracking-wider text-white/40">
      <span>Q1</span><span>Q2</span><span>Q3</span><span>Q4</span>
    </div>
  </div>
);

const FinanceMotif = ({ accent, compact, active }) => (
  <div className={`mx-auto rounded-2xl border border-dashed bg-white/[0.02] font-mono transition-colors duration-500 ${compact ? "max-w-[240px] p-4 text-[9px]" : "max-w-sm p-8 text-sm"} ${active ? "border-white/40" : "border-white/20"}`} data-testid="motif-finance">
    <p className="text-center uppercase tracking-[0.3em] text-white/40">GST Filing · GSTR-3B</p>
    <div className={`${compact ? "my-3" : "my-5"} border-t border-dashed border-white/15`} />
    {(compact ? [["Taxable value", "₹ 48,20,000"], ["CGST + SGST", "₹ 1,73,520"]] : [["Taxable value", "₹ 48,20,000"], ["IGST", "₹ 86,760"], ["CGST + SGST", "₹ 1,73,520"], ["Filed on", "11 · 06 · 2026"]]).map(([k, v]) => (
      <div key={k} className="flex justify-between py-1 text-white/70">
        <span>{k}</span><span className="text-white">{v}</span>
      </div>
    ))}
    <div className={`${compact ? "my-3" : "my-5"} border-t border-dashed border-white/15`} />
    <motion.p
      className="text-center font-bold uppercase tracking-[0.3em]"
      style={{ color: accent }}
      animate={active ? { scale: [1, 1.12, 1] } : { scale: 1 }}
      transition={active ? { duration: 0.9, repeat: Infinity } : { duration: 0.3 }}
    >
      Filed · On time
    </motion.p>
  </div>
);

const PastelMotif = ({ accent, compact, active }) => (
  <div className={`relative flex ${compact ? "h-36" : "h-64"} items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]`} data-testid="motif-pastel">
    <motion.span className={`absolute ${compact ? "h-24 w-24" : "h-44 w-44"} rounded-full`} style={{ background: `${accent}55`, filter: "blur(2px)" }} animate={{ x: [-30, 30, -30], y: [-15, 15, -15] }} transition={{ duration: active ? 2.5 : 8, repeat: Infinity, ease: "easeInOut" }} />
    <motion.span className={`absolute ${compact ? "h-16 w-16" : "h-32 w-32"} rounded-full bg-sky-300/30`} animate={{ x: [40, -40, 40], y: [20, -20, 20] }} transition={{ duration: active ? 3 : 10, repeat: Infinity, ease: "easeInOut" }} />
    <motion.span className={`absolute ${compact ? "h-12 w-12" : "h-24 w-24"} rounded-full bg-violet-300/30`} animate={{ scale: [1, 1.3, 1] }} transition={{ duration: active ? 1.8 : 6, repeat: Infinity, ease: "easeInOut" }} />
    <motion.span
      className={`relative font-serif-display italic text-white ${compact ? "text-2xl" : "text-5xl"}`}
      animate={{ scale: active ? [1, 1.08, 1] : 1 }}
      transition={active ? { duration: 1.2, repeat: Infinity } : { duration: 0.3 }}
    >
      playful.
    </motion.span>
  </div>
);

const ShopMotif = ({ accent, compact, active }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] ${compact ? "p-4" : "p-8"}`} data-testid="motif-shop">
    <div className={`grid grid-cols-3 ${compact ? "gap-2" : "gap-3"}`}>
      {["#F59E0B", "#38BDF8", "#F43F5E"].map((c, i) => (
        <motion.div
          key={c}
          className={`rounded-lg bg-white/[0.05] ${compact ? "p-2" : "p-3"}`}
          animate={active ? { y: [0, -4, 0] } : { y: 0 }}
          transition={active ? { duration: 0.8, repeat: Infinity, delay: i * 0.12 } : { duration: 0.3 }}
        >
          <div className={`${compact ? "h-8" : "h-14"} rounded-md`} style={{ background: `${c}33` }} />
          <div className="mt-2 h-1.5 w-3/4 rounded bg-white/15" />
          <div className="mt-1.5 h-1.5 w-1/2 rounded" style={{ background: accent }} />
        </motion.div>
      ))}
    </div>
    <motion.div
      className={`mt-3 rounded-full text-center font-bold text-ink ${compact ? "py-1.5 text-[9px]" : "py-2.5 text-xs"}`}
      style={{ background: accent }}
      animate={{ scale: active ? [1, 1.05, 1] : 1 }}
      transition={active ? { duration: 0.9, repeat: Infinity } : { duration: 0.3 }}
    >
      Checkout · ₹2,499
    </motion.div>
  </div>
);

const CloudMotif = ({ accent, compact, active }) => (
  <div className={`rounded-2xl border border-white/10 bg-[#0a0c14] font-mono ${compact ? "p-4 text-[9px]" : "p-8 text-xs"}`} data-testid="motif-cloud">
    {(compact ? ["api-prod-01", "cdn-edge-in", "db-primary"] : ["api-prod-01", "cdn-edge-in", "db-primary", "worker-queue"]).map((node, i) => (
      <div key={node} className={`flex items-center justify-between rounded border border-white/10 bg-ink/80 ${compact ? "mb-2 px-2.5 py-1.5" : "mb-3 px-4 py-3"}`}>
        <span className="text-white/70">{node}</span>
        <span className="flex items-center gap-2" style={{ color: accent }}>
          <motion.span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: accent }}
            animate={{ opacity: [1, 0.25, 1] }}
            transition={{ duration: active ? 0.6 : 1.6, repeat: Infinity, delay: i * 0.2 }}
          />
          healthy
        </span>
      </div>
    ))}
    <p className="text-white/55">region: ap-south-1 · autoscale on</p>
  </div>
);

const DataMotif = ({ accent, compact, active }) => {
  const path = "M0,90 L40,70 L80,78 L120,50 L160,58 L200,30 L240,38 L280,12";
  return (
    <div className={`rounded-2xl border border-white/10 bg-white/[0.02] ${compact ? "p-4" : "p-8"}`} data-testid="motif-data">
      <svg viewBox="0 0 280 100" className="w-full">
        {[25, 50, 75].map((y) => (
          <line key={y} x1="0" y1={y} x2="280" y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
        ))}
        <motion.path
          d={path}
          fill="none"
          stroke={accent}
          strokeWidth="2.5"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: active ? 0.9 : 2, ease: "easeInOut" }}
        />
        <motion.circle
          r="4"
          fill={accent}
          animate={active ? { opacity: [1, 0.3, 1] } : { opacity: 1 }}
          transition={active ? { duration: 0.7, repeat: Infinity } : { duration: 0.3 }}
        >
          <animateMotion dur={active ? "2s" : "5s"} repeatCount="indefinite" path={path} />
        </motion.circle>
      </svg>
      <div className="mt-3 flex justify-between text-[9px] uppercase tracking-wider text-white/40">
        <span>Revenue</span><span style={{ color: accent }}>▲ 38% QoQ</span>
      </div>
    </div>
  );
};

const SecurityMotif = ({ accent, compact, active }) => (
  <div className={`flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.02] ${compact ? "py-6" : "py-16"}`} data-testid="motif-security">
    <div className="relative flex items-center justify-center">
      <motion.div
        className={`absolute rounded-full border border-dashed ${compact ? "h-24 w-24" : "h-40 w-40"}`}
        style={{ borderColor: `${accent}55` }}
        animate={{ rotate: 360 }}
        transition={{ duration: active ? 4 : 14, repeat: Infinity, ease: "linear" }}
      />
      <svg viewBox="0 0 24 24" className={compact ? "h-10 w-10" : "h-16 w-16"} fill="none" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <motion.path
          d="M9 12l2 2 4-4"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4 }}
        />
      </svg>
    </div>
  </div>
);

export const MOTIFS = {
  "ui-ux-design": PastelMotif,
  "web-development": TerminalMotif,
  "mobile-apps": PhoneMotif,
  "product-development": GridMotif,
  "e-commerce": ShopMotif,
  "cloud-solutions": CloudMotif,
  "data-analytics": DataMotif,
  "cyber-security": SecurityMotif,
  "branding": BrandMotif,
  "motion-graphics": MotionMotif,
  "digital-marketing": ChartMotif,
  "finance-gst": FinanceMotif,
};

export const ServiceMotif = ({ slug, accent, compact = false, active = false }) => {
  const Motif = MOTIFS[slug];
  if (!Motif) return null;
  return <Motif accent={accent} compact={compact} active={active} />;
};
